/**
 * Responsibility: show groupings of cards as tiles, rather than the cards.
 *
 * A sub-collection and a set are the same shape to look at - a name, how far
 * along it is, and a glimpse of what is in it - so one component draws both on
 * both pages. Giving each its own grid would mean four near-identical
 * components drifting apart, which is what the shelf tile already exists to
 * avoid for cards.
 *
 * **The mosaic is a glimpse, not a summary.** Four images cannot represent two
 * hundred cards and are not trying to; they are there so a grouping is
 * recognisable at a glance without reading its name. A grouping with nothing
 * in it yet still gets a tile, because starting one before it has cards is the
 * normal way to begin.
 */
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { StateBlock } from '@kit/components/feedback';

import { atWidth } from '../collection/cardImage';

/** One grouping, whichever kind it is. */
export type Grouping = {
  /** Tag id or set label - whatever opening it needs. */
  key: string;
  name: string;
  /** What it is for, when somebody wrote it down. */
  note?: string | null;
  /** Cards owned from this grouping. */
  held: number;
  /** Cards found but not owned. Undefined where that idea does not apply. */
  wanted?: number;
  /** Cards in it altogether, or null when that cannot be known. */
  total?: number | null;
  /** A few card images, for recognising it. */
  previews: (string | null)[];
};

/** How many images a tile shows. Four fits a square without crowding. */
const PREVIEW_COUNT = 4;

/** A quarter of a small tile, so the smallest rendition that exists. */
const THUMB_WIDTH = 120;

function Mosaic({ previews }: { previews: (string | null)[] }) {
  const slots = [...previews.slice(0, PREVIEW_COUNT)];
  while (slots.length < PREVIEW_COUNT) slots.push(null);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 0.5,
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: 'action.hover',
        aspectRatio: '1 / 1',
      }}
    >
      {slots.map((src, index) => (
        <Box
          key={index}
          sx={{ bgcolor: 'action.disabledBackground', minWidth: 0, overflow: 'hidden' }}
        >
          {src ? (
            // An img rather than a CSS background, and sized through the same
            // helper every other tile uses: the stored URL carries a width in
            // its path, and the sizes this bucket does not have return 404.
            <Box
              component="img"
              src={atWidth(src, THUMB_WIDTH) ?? undefined}
              alt=""
              loading="lazy"
              sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }}
            />
          ) : null}
        </Box>
      ))}
    </Box>
  );
}

function GroupingTile({ group, onOpen }: { group: Grouping; onOpen: (key: string) => void }) {
  const total = group.total ?? null;
  const percent = total ? Math.min(100, Math.round((group.held / total) * 100)) : null;

  return (
    <Paper
      variant="outlined"
      onClick={() => onOpen(group.key)}
      sx={{
        p: 1,
        cursor: 'pointer',
        minWidth: 0,
        transition: 'border-color 140ms',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <Stack spacing={0.75} sx={{ minWidth: 0 }}>
        <Mosaic previews={group.previews} />

        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap title={group.name}>
          {group.name}
        </Typography>

        {group.note ? (
          <Typography variant="caption" color="text.secondary" noWrap title={group.note}>
            {group.note}
          </Typography>
        ) : null}

        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          <Chip size="small" label={total !== null ? `${group.held}/${total}` : `${group.held}`} />
          {/* Owned and merely found are different states, and collapsing them
              would hide whether a run is nearly done or nearly located. */}
          {group.wanted ? (
            <Chip
              size="small"
              variant="outlined"
              color="info"
              label={`${group.wanted} wanted`}
            />
          ) : null}
        </Stack>

        {percent !== null ? (
          <LinearProgress
            variant="determinate"
            value={percent}
            sx={{ height: 6, borderRadius: 1 }}
          />
        ) : null}
      </Stack>
    </Paper>
  );
}

type Props = {
  groupings: Grouping[];
  onOpen: (key: string) => void;
  emptyMessage: string;
};

export function GroupingGrid({ groupings, onOpen, emptyMessage }: Props) {
  if (groupings.length === 0) {
    return <StateBlock empty emptyMessage={emptyMessage} />;
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 1.5,
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          sm: 'repeat(3, 1fr)',
          md: 'repeat(4, 1fr)',
          lg: 'repeat(6, 1fr)',
        },
        overflowY: 'auto',
        minHeight: 0,
        alignContent: 'start',
      }}
    >
      {groupings.map((group) => (
        <GroupingTile key={group.key} group={group} onOpen={onOpen} />
      ))}
    </Box>
  );
}
